import { createColumnHelper } from "@tanstack/react-table";
import { useDispatch, useSelector } from "react-redux";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { ClientPortalGlobalSearchResultModel, ClientPortalGlobalSearchResultTypes } from "services/api/cplapi/CPLAPI";
import { useNavigate } from "react-router-dom";
import {
  selectFilteredSearchResults,
  selectSearchFilters,
  selectSearching,
  setSelectedSearchFilter,
} from "./GlobalSearchSlice";
import { buildSearchResultRedirectRoute } from "./RecordTypeRouteBuilder";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faCalendar, faCoins, faHammer, faHandshake } from "@fortawesome/pro-solid-svg-icons";
import { ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";

const GlobalSearchResultsTable: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const searching = useSelector(selectSearching);
  const results = useSelector(selectFilteredSearchResults);
  const searchFilters = useSelector(selectSearchFilters);

  function generateSearchResultTypeIcon(searchResultType: ClientPortalGlobalSearchResultTypes) {
    switch (searchResultType) {
      case ClientPortalGlobalSearchResultTypes.Case:
        return <FontAwesomeIcon icon={faBriefcase} size="lg" />;
      case ClientPortalGlobalSearchResultTypes.ProposedWorks:
        return <FontAwesomeIcon icon={faHammer} size="lg" />;
      case ClientPortalGlobalSearchResultTypes.Meeting:
        return <FontAwesomeIcon icon={faCalendar} size="lg" />;
      case ClientPortalGlobalSearchResultTypes.Transaction:
        return <FontAwesomeIcon icon={faCoins} size="lg" />;
      case ClientPortalGlobalSearchResultTypes.Job:
        return <FontAwesomeIcon icon={faHandshake} size="lg" />;
    }
  }

  const columnHelper = createColumnHelper<ClientPortalGlobalSearchResultModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "icon",
      cell: (info) => (
        <>
          {generateSearchResultTypeIcon(info.cell.row.original.type!)} {info.cell.row.original.label}
        </>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.text, {
      id: "Text",
      cell: (info) => info.getValue(),
      header: () => <>Title</>,
      enableColumnFilter: false,
    }),
    columnHelper.display({
      id: "actions",
      cell: (props) => (
        <div
          className="cpl-btn cpl-btn-primary"
          onClick={() => {
            redirectToResult(props.row.original);
          }}
          data-test-id={`search-results-table__view-record-link-${props.row.original.type}-${props.row.original.recordID}`}
        >
          View Record
        </div>
      ),
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableHiding: false,
    }),
  ];

  function redirectToResult(searchResult: ClientPortalGlobalSearchResultModel) {
    const path = buildSearchResultRedirectRoute(searchResult);
    navigate(path);
  }

  return (
    <div className="card cpl-card">
      <div className="card-body">
        <ToggleButtonGroup
          type="radio"
          name={"search-table-filter-selector"}
          onChange={(searchFilter) => dispatch(setSelectedSearchFilter(searchFilter))}
          defaultValue={searchFilters.find((i) => i.isDefault)?.value ?? searchFilters[0].value}
          className="m-2"
        >
          {searchFilters.map((searchFilter) => (
            <ToggleButton
              key={"search-table-filter-" + searchFilter.value}
              id={"search-table-filter-filter-" + searchFilter.value}
              variant={"outline-secondary"}
              value={searchFilter.value}
              className="rounded-5"
              data-test-id={`search-results-table__filter-radio-${searchFilter.value}`}
            >
              {searchFilter.displayName}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ActivityIndicator isBusy={searching}>
          <CPLTable
            data={results}
            columns={columnDefinition}
            rowMobileClickAction={() => redirectToResult}
            classNames="cpl-table-nowrap-below-desktop"
          />
        </ActivityIndicator>
      </div>
    </div>
  );
};

export default GlobalSearchResultsTable;
